import { StorageError } from '../../app/errors';
import type { MatchSetupPatch } from '../../domain/commands/types';
import type { AnchorField } from '../../domain/machine';
import type { CapturePhase, MatchAnchors, MatchRow, MatchState } from '../../types';
import { requireTransaction } from '../commit';
import { db, TABLE } from '../db';

/**
 * The `matches` repo.
 *
 * Two things here are mechanisms rather than conveniences:
 *
 * 1. **`writeSyncSuccess` exists; there is no `writeSyncFailure`.** SYN-3 requires a failed push to
 *    leave `lastSyncedTs` unchanged. Design §11.2 makes that a compile-time property by giving
 *    success and failure different patch key unions; at the storage layer the same property is
 *    "the failure path has no function to call". A failure writes `attempts` / `lastError` on the
 *    outbox row and touches nothing here.
 * 2. **`writeAnchors` is one call for all four anchors.** SES-8's boundary adjustment re-derives
 *    every dependent minute in the same transaction (§10.1); an anchor written on its own would
 *    leave the match's minutes derived from a mixture of old and new anchors, which is the one state
 *    no screen could render honestly.
 */

/** @throws {StorageError} outside a transaction, on a duplicate id, or on a write failure. */
export async function insertMatch(row: MatchRow): Promise<void> {
  requireTransaction('insertMatch');
  try {
    await db().matches.add(row);
  } catch (cause) {
    throw new StorageError('Could not create the match.', {
      cause,
      context: { store: TABLE.matches, id: row.id, teamId: row.teamId },
    });
  }
}

/**
 * What a SES-1 transition writes: the new state, at most one anchor, and (for Abandon) the flag.
 *
 * `anchorTs` is **the moment the sustained press began**, not when the hold completed (§2.2) — the
 * caller supplies it, because a pure handler may not read a clock and this repo may not invent one.
 */
export interface TransitionPatch {
  state: MatchState;
  anchor?: { field: AnchorField; ts: number };
  /** F-12 — Abandon lands in `fullTime` with this set. */
  abandoned?: boolean;
}

/**
 * Applies one state transition.
 *
 * @throws {StorageError} outside a transaction, if the match is missing, or on a write failure.
 */
export async function applyTransition(id: string, patch: TransitionPatch): Promise<void> {
  requireTransaction('applyTransition');
  const row = await requireMatch(id);
  const next: MatchRow = { ...row, state: patch.state };
  if (patch.anchor) next[patch.anchor.field] = patch.anchor.ts;
  if (patch.abandoned !== undefined) next.abandoned = patch.abandoned;
  await putMatch(next, 'transition');
}

/**
 * PRE-1 / SES-5 — the Pre-Match setup fields, and only those (build unit U-7).
 *
 * The storage-layer mirror of `MatchSetupPatch`'s key union, for the same reason `writeCountState`
 * and `writePositions` are two functions: a setup edit and a state transition write disjoint sets of
 * columns, so neither can reach the other's. `state`, `capturePhase`, `lockTs`, `abandoned`, the
 * four anchors and every sync field are unreachable from here.
 *
 * A key left `undefined` is untouched — the screen writes one control at a time, and a spread that
 * treated absent as `null` would clear the competition the moment the user changed the date.
 *
 * @throws {StorageError} outside a transaction, if the match is missing, or on a write failure.
 */
export async function updateSetup(id: string, patch: MatchSetupPatch): Promise<void> {
  requireTransaction('updateSetup');
  const row = await requireMatch(id);
  const next: MatchRow = { ...row };
  if (patch.teamId !== undefined) next.teamId = patch.teamId;
  if (patch.comp !== undefined) next.comp = patch.comp;
  if (patch.date !== undefined) next.date = patch.date;
  // PRE-8. The handler decides whether `formationGuess` travels with it (§6.0a's reconciliation) —
  // this applies whatever the one op carries, so the pair lands in a single `put`.
  if (patch.format !== undefined) next.format = patch.format;
  // B §4.1 / F-2 (ruled) — the patch-field extension. Nothing rides along with it: unlike
  // `format`, a purpose change clears no guess and moves no default, so one line is the whole
  // of the storage half.
  if (patch.purpose !== undefined) next.purpose = patch.purpose;
  if (patch.halfLengthMin !== undefined) next.halfLengthMin = patch.halfLengthMin;
  if (patch.formationGuess !== undefined) next.formationGuess = patch.formationGuess;
  await putMatch(next, 'setup update');
}

/**
 * SES-8 — rewrites all four clock anchors together.
 *
 * The caller re-derives every Observation and MatchNote minute in the **same** transaction
 * (§10.1, §2.4). This function deliberately does not do that itself: the minutes come from
 * `domain/clock.ts`, and `data/` computing a derived value is how a second derivation path is born.
 *
 * @throws {StorageError} outside a transaction, if the match is missing, or on a write failure.
 */
export async function writeAnchors(
  id: string,
  anchors: Omit<MatchAnchors, 'halfLengthMin'>,
): Promise<void> {
  requireTransaction('writeAnchors');
  const row = await requireMatch(id);
  await putMatch({ ...row, ...anchors }, 'anchor adjustment');
}

/**
 * LOCK-4 — `capturePhase` moves `discovery → locked` once, and never back.
 *
 * The one-way rule is enforced here as well as in the machine: a second lock would be a second
 * `boardPosition` assignment, which INV-4 forbids ("assigned exactly once per lock or per explicit
 * user swap"), and the positions would already be committed by the time anything noticed.
 *
 * @throws {StorageError} outside a transaction, if the match is missing, if it is already locked, or
 *   on a write failure.
 */
export async function confirmLock(id: string, lockTs: number): Promise<void> {
  requireTransaction('confirmLock');
  const row = await requireMatch(id);
  const locked: CapturePhase = 'locked';
  if (row.capturePhase === locked) {
    throw new StorageError(
      `INV-4: match ${id} is already locked; a second lock would assign board positions twice.`,
      { context: { store: TABLE.matches, id, lockTs: row.lockTs } },
    );
  }
  await putMatch({ ...row, capturePhase: locked, lockTs }, 'lock');
}

/** DIST-1 — passing triage, possibly empty. @throws {StorageError} */
export async function writeTriagePassed(id: string, ts: number): Promise<void> {
  requireTransaction('writeTriagePassed');
  const row = await requireMatch(id);
  await putMatch({ ...row, triagePassedTs: ts }, 'triage pass');
}

/**
 * The **only** writer of `lastSyncedTs` — see the module note.
 *
 * Called on a 2xx *and* when the §11.3 manifest comparison finds the server's copy already current:
 * skipping a redundant PUT must still advance the timestamp, or SYN-6 reports a protected match as
 * unprotected for the rest of its life.
 *
 * @throws {StorageError} outside a transaction, if the match is missing, or on a write failure.
 */
export async function writeSyncSuccess(id: string, ts: number, hash: string): Promise<void> {
  requireTransaction('writeSyncSuccess');
  const row = await requireMatch(id);
  await putMatch({ ...row, lastSyncedTs: ts, lastSyncedHash: hash }, 'sync success');
}

/** INV-8's deletion path. Named for its single caller. @throws {StorageError} */
export async function deleteMatchesForCascade(ids: readonly string[]): Promise<void> {
  requireTransaction('deleteMatchesForCascade');
  if (ids.length === 0) return;
  try {
    await db().matches.bulkDelete([...ids]);
  } catch (cause) {
    throw new StorageError('Could not delete the matches during a cascade.', {
      cause,
      context: { store: TABLE.matches, count: ids.length },
    });
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Reads
// ─────────────────────────────────────────────────────────────────────────────

/** @throws {StorageError} on a read failure. */
export async function getMatch(id: string): Promise<MatchRow | undefined> {
  try {
    return await db().matches.get(id);
  } catch (cause) {
    throw new StorageError('Could not read the match.', {
      cause,
      context: { store: TABLE.matches, id },
    });
  }
}

/** FILE-3 — a team's matches, newest first by calendar date. @throws {StorageError} */
export async function listMatchesForTeam(teamId: string): Promise<MatchRow[]> {
  try {
    return await db()
      .matches.where('[teamId+date]')
      .between([teamId, ''], [teamId, '￿'])
      .reverse()
      .toArray();
  } catch (cause) {
    throw new StorageError('Could not read the team’s matches.', {
      cause,
      context: { store: TABLE.matches, teamId },
    });
  }
}

/**
 * Matches in any of the given states.
 *
 * SES-9's exclusivity check reads the in-match states; SES-11's Home reads `fullTime`/`triage`/
 * `distill`; PRE-5's carry-forward reads **every** state, deliberately (a prior match that is not
 * `archived` is never excluded — §17.1).
 *
 * @throws {StorageError} on a read failure.
 */
export async function listMatchesByState(states: readonly MatchState[]): Promise<MatchRow[]> {
  if (states.length === 0) return [];
  try {
    return await db()
      .matches.where('state')
      .anyOf([...states])
      .toArray();
  } catch (cause) {
    throw new StorageError('Could not read matches by state.', {
      cause,
      context: { store: TABLE.matches, states },
    });
  }
}

/** @throws {StorageError} on a read failure. */
export async function listAllMatches(): Promise<MatchRow[]> {
  try {
    return await db().matches.toArray();
  } catch (cause) {
    throw new StorageError('Could not read the match list.', { cause });
  }
}

// ─────────────────────────────────────────────────────────────────────────────

async function requireMatch(id: string): Promise<MatchRow> {
  const row = await db().matches.get(id);
  if (!row) {
    throw new StorageError(`Match ${id} no longer exists; nothing was written.`, {
      context: { store: TABLE.matches, id },
    });
  }
  return row;
}

async function putMatch(row: MatchRow, what: string): Promise<void> {
  try {
    await db().matches.put(row);
  } catch (cause) {
    throw new StorageError(`Could not write the match ${what}.`, {
      cause,
      context: { store: TABLE.matches, id: row.id },
    });
  }
}
